import React, { useEffect, useState, useRef } from 'react';
import axios from '../../api/axios';
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import { TrashIcon, PhoneIcon, MapPinIcon, UserIcon } from "@heroicons/react/24/outline";
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import Loader from '../../components/Loader';

const ViewSales = () => {
  const navigate = useNavigate();
  const { saleId } = useParams();
  const [loading, setLoading] = useState(false);

  const [items, setItems] = useState([]);
  const [invoice, setInvoice] = useState('');
  const [saleDate, setSaleDate] = useState(new Date());
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [status, setStatus] = useState('quotation');
  const [reference, setReference] = useState('');
  const [saleItems, setSaleItems] = useState([]);
  const [discountType, setDiscountType] = useState('flat');
  const [globalDiscount, setGlobalDiscount] = useState(0);
  const [otherCharges, setOtherCharges] = useState(0);
  const [note, setNote] = useState('');
  const [paymentList, setPaymentList] = useState([]);
  const [paymentBank, setPaymentBank] = useState('');
  const [createdSaleId, setCreatedSaleId] = useState(null);
  const [paidAmount, setPaidAmount] = useState(0);

  useEffect(() => {
    axios.get('/items').then(res => setItems(res.data));
    if (!saleId) return;

    setLoading(true);
    axios.get(`/sales/${saleId}`)
      .then(async (res) => {
        const sale = res.data;

        setStatus(sale.status);
        setReference(sale.reference);
        setSaleDate(new Date(sale.sale_date));
        setOtherCharges(Number(sale.other_charges || 0));
        setGlobalDiscount(Number(sale.discount || 0));
        setDiscountType(sale.discount_type || 'flat');
        setPaidAmount(Number(sale.paid_amount || 0));
        setNote(sale.note || '');
        setPaymentBank(sale.bank_id || '');
        setInvoice(sale.id);

        // Map in sizes + colors from API
        const mapped = (sale.items || []).map((item) => {
          const id = Number(item.item_id);
          const price = Number(item.price || 0);
          const discount = Number(item.discount || 0);

          const sizes = Array.isArray(item.sizes)
            ? item.sizes.map(s => ({ size: s.size, quantity: Number(s.quantity || 0) }))
            : [];

          const colors = Array.isArray(item.colors)
            ? item.colors.map(c => ({ color: c.color, quantity: Number(c.quantity || 0) }))
            : [];

          // Quantity: from sizes if we have sizes, otherwise from colors
          const qtyFromSizes = sizes.reduce((sum, s) => sum + Number(s.quantity || 0), 0);
          const qtyFromColors = colors.reduce((sum, c) => sum + Number(c.quantity || 0), 0);
          const quantity = sizes.length ? qtyFromSizes : qtyFromColors;

          const total = (price - discount) * quantity;

          return {
            id,
            item_id: id,
            name: item.name,
            price,
            discount,
            sizes,
            colors,
            quantity,
            total
          };
        });

        setSaleItems(mapped);

        // Extra loads
        const cust = await axios.get(`/customers/${sale.customer_id}`);
        setSelectedCustomer(cust.data);

        const payments = await axios.get(`/sales/${saleId}/payments`);
        setPaymentList(payments.data);
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [saleId]);

  // Totals
  const subtotal = saleItems.reduce((sum, it) => sum + Number(it.total || 0), 0);
  const subquantity = saleItems.reduce((sum, it) => sum + Number(it.quantity || 0), 0);
  const subprice = saleItems.reduce((sum, it) => sum + Number(it.price || 0), 0);
  const subdiscount = saleItems.reduce((sum, it) => sum + Number(it.discount || 0), 0);

  const discountOnAll =
    discountType === 'percent'
      ? (subtotal * Number(globalDiscount || 0)) / 100
      : Number(globalDiscount || 0);

  const grandTotal = subtotal + Number(otherCharges || 0) - discountOnAll;
  const dueAmount = Math.max(grandTotal - Number(paidAmount || 0), 0).toFixed(2);
  const paymentStatus =
    Number(paidAmount || 0) >= grandTotal
      ? 'paid'
      : Number(paidAmount || 0) > 0
        ? 'partial'
        : 'unpaid';

  const capitalizeFirstLetter = (word) =>
    !word || typeof word !== 'string' ? word : word.charAt(0).toUpperCase() + word.slice(1);

  return (
    <div className="relative p-4 max-w-6xl mx-auto bg-white rounded">
      <div className='flex justify-between items-center border-b-4 mb-3 pb-2'>
        <h2 className="text-xl font-bold">Sales Details: INV-{invoice}</h2>
        <p>Date: {moment(saleDate).format("DD-MM-YYYY hh:mm:ss A")}</p>
      </div>

      <div className="space-y-4">
        {/* Top Info */}
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div className='flex flex-col'>
            <span className="font-semibold text-sm">From</span>
            <span className='text-[12px] font-medium flex justify-start items-center'><UserIcon color='blue' className="w-3 h-3 mr-1" />MaanRishfa Traders</span>
            <span className='text-[12px] flex justify-start items-center'><PhoneIcon color='blue' className="w-3 h-3 mr-1" />0192-5325050</span>
            <span className='text-[12px] italic flex justify-start items-center'><MapPinIcon color='blue' className="w-3 h-3 mr-1" />Noapara, Abhaynagar, Jessore</span>
          </div>
          <div className='flex flex-col'>
            <span className="font-semibold text-sm">Customer Details</span>
            <span className='text-[12px] font-medium flex justify-start items-center'><UserIcon color='blue' className="w-3 h-3 mr-1" />{selectedCustomer?.name}</span>
            <span className='text-[12px] flex justify-start items-center'><PhoneIcon color='blue' className="w-3 h-3 mr-1" />{selectedCustomer?.phone ?? "N/A"}</span>
            <span className='text-[12px] italic flex justify-start items-center'><MapPinIcon color='blue' className="w-3 h-3 mr-1" />{selectedCustomer?.address ?? "N/A"}</span>
          </div>
          <div className='flex flex-col items-end'>
            <span className='font-semibold text-sm flex justify-start items-center'>Invoice No: INV-{invoice}</span>
            <span className='text-[13px] flex justify-start items-center'>
              Sales Status:
              <span className={`ml-1 ${status === "final" ? " text-green-600 font-semibold" : "text-red-500 font-semibold"}`}>
                {capitalizeFirstLetter(status) ?? "N/A"}
              </span>
            </span>
            <span className='text-[13px] flex justify-start items-center'>
              Payment Status:
              <span className={`ml-1 ${paymentStatus === "partial" ? " text-blue-700 font-semibold" : paymentStatus === "unpaid" ? "text-red-500 font-semibold" : "text-green-500 font-semibold"}`}>
                {capitalizeFirstLetter(paymentStatus) ?? "N/A"}
              </span>
            </span>
            <span className='text-[13px] italic flex justify-start items-center'>Reference: <span className='ml-1 font-semibold'>{reference ?? "N/A"}</span></span>
          </div>
        </div>

        {/* Items Table */}
        {saleItems.length > 0 && (
          <table className="w-full mt-4 border text-xs">
            <thead className="bg-blue-800 text-white">
              <tr>
                <th className="border px-2 py-1">Item Name</th>
                <th className="border px-2 py-1">Sizes</th>
                <th className="border px-2 py-1">Colors</th>
                <th className="border px-2 py-1">Quantity</th>
                <th className="border px-2 py-1">Unit Price</th>
                <th className="border px-2 py-1">Discount</th>
                <th className="border px-2 py-1">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {saleItems.map(item => (
                <tr className='odd:bg-white even:bg-gray-100' key={item.id}>
                  <td className="border px-2 py-1 font-medium">{item.name}</td>

                  {/* Sizes */}
                  <td className="border px-2 py-1">
                    <div className="flex flex-wrap gap-2">
                      {item.sizes?.length ? item.sizes.map((s, i) => (
                        <span key={i} className="flex gap-1 items-center">
                          <span>{s.size}:</span>
                          <span>{s.quantity}</span>
                        </span>
                      )) : <span className="text-gray-400">—</span>}
                    </div>
                  </td>

                  {/* Colors */}
                  <td className="border px-2 py-1">
                    <div className="flex flex-wrap gap-2">
                      {item.colors?.length ? item.colors.map((c, i) => (
                        <span key={i} className="flex gap-1 items-center">
                          <span className="capitalize">{c.color}:</span>
                          <span>{c.quantity}</span>
                        </span>
                      )) : <span className="text-gray-400">—</span>}
                    </div>
                  </td>

                  {/* Quantity once (from sizes or colors) */}
                  <td className="border px-2 py-1 text-center font-medium">{Number(item.quantity || 0)}</td>

                  <td className="border px-2 py-1 text-end">{Number(item.price).toFixed(2)}</td>
                  <td className="border px-2 py-1 text-end">{Number(item.discount).toFixed(2)}</td>
                  <td className="border px-2 py-1 text-right font-semibold">{Number(item.total).toFixed(2)}</td>
                </tr>
              ))}
              <tr className='bg-blue-300'>
                <td className="border px-2 py-1 text-black font-bold text-end" colSpan={3}>Total:</td>
                <td className="border px-2 py-1 text-center font-bold">{Number(subquantity).toFixed(2)}</td>
                <td className="border px-2 py-1 text-end font-bold">৳ {Number(subprice).toFixed(2)}</td>
                <td className="border px-2 py-1 text-end font-bold">৳ {Number(subdiscount).toFixed(2)}</td>
                <td className="border px-2 py-1 text-right font-bold">৳ {Number(subtotal).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        )}

        <div className='flex justify-between items-start'>
          <div className='flex flex-col md:w-2/3'>
            <p className='text-blue-800 mb-0 mt-2 text-md'>Payments Information :</p>
            <table className="w-full mt-2 border text-xs">
              <thead className="bg-blue-800 text-white">
                <tr>
                  <th className="border px-2 py-1">#</th>
                  <th className="border px-2 py-1">Date</th>
                  <th className="border px-2 py-1">Bank Name</th>
                  <th className="border px-2 py-1">Amount</th>
                  <th className="border px-2 py-1">Type</th>
                </tr>
              </thead>
              {paymentList.length > 0 && paymentList.map((item, i) => (
                <tr className='odd:bg-white even:bg-gray-100' key={i}>
                  <td className="border px-2 py-1 font-medium">{i + 1}</td>
                  <td className="border px-2 py-1 text-center">{moment(item.created_at).format("DD-MM-YYYY hh:mm:ss A")}</td>
                  <td className="border px-2 py-1 text-center">{item.bank_name}</td>
                  <td className="border px-2 py-1 text-center">{Number(item.amount).toFixed(2)}</td>
                  <td className="border px-2 py-1 text-center">{capitalizeFirstLetter(item.type)}</td>
                </tr>
              ))}
            </table>

            <div className="flex justify-start gap-4 mt-4">
              <button type='button' onClick={() => navigate(`/sales/edit/${saleId}`)} className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-1 rounded">Edit</button>
              <button type="button" onClick={() => navigate('/sales-list')} className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-1 rounded">Close</button>
            </div>
          </div>

          <div className='flex w-full md:w-1/3 md:justify-end'>
            <div className="text-right mt-6 space-y-1">
              <div>Subtotal: <strong>{subtotal.toFixed(2)}</strong></div>
              <div>Other Charges: <strong>{Number(otherCharges).toFixed(2)}</strong></div>
              <div>Discount: <strong>{discountOnAll.toFixed(2)}</strong></div>
              <div className="font-semibold">Grand Total: <strong>{grandTotal.toFixed(2)} BDT</strong></div>
              <div className="font-semibold">Paid Amount: <strong className='text-green-700'>{Number(paidAmount).toFixed(2)} BDT</strong></div>
              <div className="font-semibold">Due Amount: <strong className='text-red-600'>{dueAmount} BDT</strong></div>
            </div>
          </div>
        </div>

        {createdSaleId && (
          <div className="mt-6 text-right">
            <a href={`/invoices/sales/${createdSaleId}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline mr-4">View Invoice</a>
            <a href={`/invoices/sales/print/${createdSaleId}`} target="_blank" rel="noreferrer" className="text-green-600 hover:underline">Print Invoice</a>
          </div>
        )}
      </div>

      <Loader show={loading} />
    </div>
  );
};

export default ViewSales;